import type {
  ChangeAndroidAppDataPayload,
  ChangeIOSAppContactsPayload,
  ChangeIOSAppDataPayload,
  ChangeMainDataPayload,
  DeleteImageActionParams,
  DeleteImagePayload,
  DeleteImageResponse,
  GetAppData,
  GetAppDataSuccessResponse,
  IImage,
  UploadAppImagesResponse,
  UploadIOSAndroidAppImages,
  AppState,
  iOSServerBody,
  AndroidServerBody,
  WebServerBody,
} from '@/types/app'
import type { DefaultError } from '@/types/httpError'
import type { RootActions, RootGetters, RootState } from './types'
import {
  androidAppDataChangeApiCall,
  androidAppUploadImagesApiCall,
  deleteImageApiCall,
  getAppDataApiCall,
  iOSAppContactsChangeApiCall,
  iOSAppDataChangeApiCall,
  iosAppUploadImagesApiCall,
  mainAppDataChangeApiCall,
  mainAppUploadImagesApiCall,
  webAppUploadImagesApiCall,
  webAppDataChangeApiCall,
} from '@/api/app'
import { useApiCall } from '@/composables/useApiCall'
import { defineStore } from 'pinia'

export const useAppStore = defineStore<string, RootState, RootGetters, RootActions>('app', {
  state() {
    return {
      main: null,
      iosApp: null,
      androidApp: null,
      webApp: null,
      publicationsTab: 'ios',
      error: null,
      tempAppIcons: {
        ios: '',
        android: '',
      },
      saveIOSMain: null,
      saveIOSExtra: null,
      saveIOSImages: null,
      saveAndroidData: null,
      saveWebData: null,
    }
  },
  getters: {
    getMainAppData(state) {
      return state.main
    },
    getiOSAppData(state) {
      return state.iosApp
    },
    getAndroidAppData(state) {
      return state.androidApp
    },
    getAppState(state) {
      const appState = state.main?.state
      return appState
        ? (appState
            .split(/\s/g)
            .map(
              (str, i) => `${i > 0 ? str[0].toUpperCase() : str[0].toLowerCase()}${str.slice(1)}`,
            )
            .join('') as AppState)
        : ''
    },
  },
  actions: {
    setMainAppData(payload: GetAppData) {
      const { iconName, companyName, logoPdf, logoPng, logoSvg, splashScreenText, jivo, ...rest }
        = payload

      this.main = {
        ...rest,
        iconName: iconName ?? '',
        companyName: companyName ?? '',
        logo: {
          svg: logoSvg,
          pdf: logoPdf,
          png: logoPng,
        },
        splashScreenText: splashScreenText ?? '',
        jivo: jivo ?? '',
      }
    },
    setAppState(payload: AppState) {
      if (this.main) {
        this.main.state = payload
      }
    },
    setIOSAppData(iosSettings: iOSServerBody) {
      const {
        name,
        address,
        email,
        phone,
        copyright,
        title,
        userAgreementUrl,
        supportUrl,
        description,
        keyWords,
        appIcon,
        ...rest
      } = iosSettings
      this.iosApp = {
        contacts: {
          name: name ?? '',
          address: address ?? '',
          email: email ?? '',
          phone: phone ?? '',
          copyright: copyright ?? '',
        },
        title: title ?? '',
        userAgreementUrl: userAgreementUrl ?? '',
        supportUrl: supportUrl ?? '',
        description: description ?? '',
        keyWords: keyWords ?? '',
        appIcon,
        images: rest,
      }
    },
    setAndroidAppData(androidSettings: AndroidServerBody) {
      const {
        androidAppIcon,
        title,
        shortDescription,
        description,
        userAgreementUrl,
        userDeleteFormUrl,
        defaultLanguage,
        appCategory,
        marketBanner,
        marketImage,
      } = androidSettings
      this.androidApp = {
        appIcon: Array.isArray(androidAppIcon) ? androidAppIcon[0] : androidAppIcon,
        title: title ?? '',
        shortDescription: shortDescription ?? '',
        description: description ?? '',
        userAgreementUrl: userAgreementUrl ?? '',
        userDeleteFormUrl: userDeleteFormUrl ?? '',
        defaultLanguage: defaultLanguage ?? '',
        appCategory: appCategory ?? '',
        images: {
          marketBanner,
          marketImage,
        },
      }
    },
    setWebAppData(webSettings: WebServerBody) {
      const { domain, favicon180, favicon32, favicon16, logoPng } = webSettings

      this.webApp = {
        domain: domain ?? '',
        favicon_180: {
          size: '180',
          id: favicon180?.id ?? null,
          preview: favicon180?.originalUrl ?? '',
          file: null,
        },
        favicon_32: {
          size: '32',
          id: favicon32?.id ?? null,
          preview: favicon32?.originalUrl ?? '',
          file: null,
        },
        favicon_16: {
          size: '16',
          id: favicon16?.id ?? null,
          preview: favicon16?.originalUrl ?? '',
          file: null,
        },
        logo: {
          id: logoPng?.id ?? null,
          preview: logoPng?.originalUrl ?? '',
          file: null,
        },
      }
    },
    setError(error: DefaultError) {
      this.error = error
    },
    setTempIcons(key: 'ios' | 'android', link: string) {
      this.tempAppIcons[key] = link
    },
    resetTempIcons() {
      this.tempAppIcons = {
        ios: '',
        android: '',
      }
    },
    async getAppData() {
      const {
        data: appData,
        error: appError,
        executeApiCall: getAppDataAction,
      } = useApiCall<GetAppDataSuccessResponse, DefaultError>(getAppDataApiCall, true)

      try {
        await getAppDataAction()
        if (appData.value) {
          const data = appData.value.data
          this.setIOSAppData(data.iosSettings)
          this.setMainAppData(data)
          this.setAndroidAppData(data.androidSettings)
          this.setWebAppData(data.webSettings)
        }
      } catch {
        if (appError.value?.data) {
          this.setError(appError.value.data)
        }
      }
    },
    async changeMainAppData(params: ChangeMainDataPayload) {
      const {
        data: mainAppData,
        executeApiCall: changeMainData,
        error: mainAppError,
      } = useApiCall<GetAppDataSuccessResponse, DefaultError, ChangeMainDataPayload>(
        mainAppDataChangeApiCall,
        true,
        params,
      )
      try {
        await changeMainData()

        if (mainAppData.value) {
          this.setMainAppData(mainAppData.value.data)
        }
      } catch {
        if (mainAppError.value) {
          this.setError(mainAppError.value.data)
          throw mainAppError.value.data
        }
      }
    },
    async changeIOSAppData(params: Partial<ChangeIOSAppDataPayload>) {
      const {
        data: changeIOSDataResponse,
        executeApiCall: changeIOSData,
        error: changeIOSDataError,
      } = useApiCall<GetAppDataSuccessResponse, DefaultError, Partial<ChangeIOSAppDataPayload>>(
        iOSAppDataChangeApiCall,
        true,
        params,
      )

      try {
        await changeIOSData()
        if (changeIOSDataResponse.value) {
          this.setIOSAppData(changeIOSDataResponse.value.data.iosSettings)
        }
      } catch {
        if (changeIOSDataError.value) {
          // if ('data' in changeIOSDataError.value?.data) {
          //   this.error = changeIOSDataError.value?.data.data
          //   return
          // }
          this.setError(changeIOSDataError.value.data)
          throw changeIOSDataError.value.data
        }
      }
    },
    async changeIOSAppContacts(params: ChangeIOSAppContactsPayload) {
      const {
        data: changeIOSContactsResponse,
        executeApiCall: changeIOSContacts,
        error: changeIOSContactsError,
      } = useApiCall<GetAppDataSuccessResponse, DefaultError, ChangeIOSAppContactsPayload>(
        iOSAppContactsChangeApiCall,
        true,
        params,
      )

      try {
        await changeIOSContacts()
        if (changeIOSContactsResponse.value) {
          this.setIOSAppData(changeIOSContactsResponse.value.data.iosSettings)
        }
      } catch {
        if (changeIOSContactsError.value) {
          // if ('data' in changeIOSContactsError.value?.data) {
          //   this.error = changeIOSContactsError.value?.data.data
          //   return
          // }
          this.setError(changeIOSContactsError.value.data)
          throw changeIOSContactsError.value.data
        }
      }
    },
    async changeAndroidAppData(params: ChangeAndroidAppDataPayload) {
      const {
        data: changeAndroidDataResponse,
        executeApiCall: changeAndroidData,
        error: changeAndroidDataError,
      } = useApiCall<GetAppDataSuccessResponse, DefaultError, ChangeAndroidAppDataPayload>(
        androidAppDataChangeApiCall,
        true,
        params,
      )

      try {
        await changeAndroidData()
        if (changeAndroidDataResponse.value) {
          this.setAndroidAppData(changeAndroidDataResponse.value.data.androidSettings)
        }
      } catch {
        if (changeAndroidDataError.value) {
          // if ('data' in changeAndroidDataError.value) {
          //   this.error = changeAndroidDataError.value.data.data
          //   return
          // }
          this.setError(changeAndroidDataError.value.data)
          throw changeAndroidDataError.value.data
        }
      }
    },
    async changeWebAppData(params: { domain: string }) {
      const {
        data: changeWebDataResponse,
        executeApiCall: changeWebData,
        error: changeWebDataError,
      } = useApiCall<GetAppDataSuccessResponse, DefaultError, { domain: string }>(
        webAppDataChangeApiCall,
        true,
        params,
        false,
      )
      try {
        await changeWebData()
        if (changeWebDataResponse.value) {
          this.setWebAppData(changeWebDataResponse.value.data.webSettings)
        }
      } catch {
        if (changeWebDataError.value) {
          throw changeWebDataError.value.data
        }
      }
    },
    async uploadMainAppIcons(params: FormData) {
      const {
        data: appData,
        executeApiCall: uploadMainAppIcons,
        error: appError,
      } = useApiCall<UploadAppImagesResponse, DefaultError, FormData>(
        mainAppUploadImagesApiCall,
        true,
        params,
        false,
      )
      try {
        await uploadMainAppIcons()
        if (appData.value) {
          this.setMainAppData(appData.value.data)
        }
      } catch {
        if (appError.value) {
          this.error = appError.value.data
          throw appError.value.data
        }
      }
    },
    async uploadIOSAppImages(params: FormData) {
      const {
        data: appData,
        executeApiCall: uploadIOSAppImages,
        error: appError,
      } = useApiCall<UploadIOSAndroidAppImages, DefaultError, FormData>(
        iosAppUploadImagesApiCall,
        true,
        params,
        false,
      )
      try {
        await uploadIOSAppImages()
        return appData.value?.data as IImage[]
      } catch {
        if (appError.value) {
          this.error = appError.value.data
          throw appError.value.data
        }
      }
      return null
    },
    async uploadAndroidAppImages(params: FormData) {
      const {
        data: appData,
        executeApiCall: uploadAndroidAppImages,
        error: appError,
      } = useApiCall<UploadIOSAndroidAppImages, DefaultError, FormData>(
        androidAppUploadImagesApiCall,
        true,
        params,
        false,
      )
      try {
        await uploadAndroidAppImages()
        return appData.value?.data as IImage[]
      } catch {
        if (appError.value) {
          this.error = appError.value.data
          throw appError.value.data
        }
        return null
      }
    },
    async uploadWebAppImages(params: FormData) {
      const {
        data: changeWebDataResponse,
        executeApiCall: changeWebData,
        error: changeWebDataError,
      } = useApiCall<GetAppDataSuccessResponse, DefaultError, FormData>(
        webAppUploadImagesApiCall,
        true,
        params,
        false,
      )
      try {
        await changeWebData()
        if (changeWebDataResponse.value) {
          this.setWebAppData(changeWebDataResponse.value.data.webSettings)
        }
      } catch {
        if (changeWebDataError.value) {
          throw changeWebDataError.value.data
        }
      }
    },
    async deleteAppImage(params: DeleteImageActionParams) {
      const { executeApiCall: deleteImage, error: deleteImageError } = useApiCall<
        DeleteImageResponse,
        DefaultError,
        DeleteImagePayload
      >(deleteImageApiCall, true, params)

      try {
        await deleteImage()
      } catch {
        if (deleteImageError.value) {
          this.error = deleteImageError.value.data
        }
        throw deleteImageError.value?.data
      }
    },
  },
})
